// ── TimeCalendarView ─────────────────────────────────────────────────────────
// Weekly calendar grid for time tracking. Supports drag-to-create new entries,
// drag-to-move, drag-to-resize, overlap layout, current-time indicator,
// quick-create popover, and undo toasts.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Clock, Loader2 } from "lucide-react";
import {
  HOUR_HEIGHT,
  START_HOUR,
  END_HOUR,
  TOTAL_HOURS,
  BUSINESS_START,
  SNAP_MINUTES,
  MIN_BLOCK_MINUTES,
  getMatterColor,
  getEntryPosition,
  computeTimeEntryOverlap,
  snapToGrid,
  formatHourLabel,
  minutesToTimeStr,
  formatDuration,
  getWeekDays,
  isSameDay,
  isToday,
  formatShortDate,
  formatWeekday,
  toISODateStr,
} from "./timeCalendarUtils";
import TimeCalendarQuickCreate from "./TimeCalendarQuickCreate";
import TimeCalendarEntryDetail from "./TimeCalendarEntryDetail";
import { timeEntriesApi } from "../../services/api";

// ── Props ────────────────────────────────────────────────────────────────────

interface TimeCalendarViewProps {
  weekStart: Date;
  setWeekStart: (d: Date) => void;
  entries: any[];
  matters: any[];
  clients: any[];
  loading: boolean;
  onRefresh: () => void;
  selectedEntry: any | null;
  onSelectEntry: (entry: any | null) => void;
}

// ── Component ────────────────────────────────────────────────────────────────

const TimeCalendarView: React.FC<TimeCalendarViewProps> = ({
  weekStart,
  setWeekStart,
  entries,
  matters,
  clients,
  loading,
  onRefresh,
  selectedEntry,
  onSelectEntry,
}) => {
  // ── Drag-create state ──────────────────────────────────────────────────────

  const [dragCreate, setDragCreate] = useState<{
    dayIndex: number;
    startMinutes: number;
    currentMinutes: number;
  } | null>(null);

  // ── Quick-create popover data (shown after drag-create release) ────────────

  const [quickCreateData, setQuickCreateData] = useState<{
    date: string;
    startTime: string;
    minutes: number;
    anchorX: number;
    anchorY: number;
  } | null>(null);

  // ── Drag-move state ────────────────────────────────────────────────────────

  const [dragMove, setDragMove] = useState<{
    entryId: string;
    offsetMinutes: number;
    dayOffset: number;
  } | null>(null);

  // ── Drag-resize state ──────────────────────────────────────────────────────

  const [dragResize, setDragResize] = useState<{
    entryId: string;
    originalMinutes: number;
    newMinutes: number;
  } | null>(null);

  // ── Undo toast ─────────────────────────────────────────────────────────────

  const [undoAction, setUndoAction] = useState<{
    message: string;
    rollback: () => Promise<void>;
  } | null>(null);

  // ── Refs ───────────────────────────────────────────────────────────────────

  const gridRef = useRef<HTMLDivElement>(null);
  const columnRefs = useRef<(HTMLDivElement | null)[]>([]);
  const dragCreateRef = useRef<{ startY: number } | null>(null);

  // ── Auto-scroll to business hours on mount ─────────────────────────────────

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.scrollTop = BUSINESS_START * HOUR_HEIGHT;
    }
  }, []);

  // ── Current time indicator ─────────────────────────────────────────────────

  const [currentTimeTop, setCurrentTimeTop] = useState(0);

  useEffect(() => {
    const update = () => {
      const now = new Date();
      setCurrentTimeTop(
        ((now.getHours() * 60 + now.getMinutes()) / 60) * HOUR_HEIGHT
      );
    };
    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, []);

  // ── Undo toast auto-dismiss ────────────────────────────────────────────────

  useEffect(() => {
    if (!undoAction) return;
    const t = setTimeout(() => setUndoAction(null), 4000);
    return () => clearTimeout(t);
  }, [undoAction]);

  // ── Derived data ───────────────────────────────────────────────────────────

  const weekDays = useMemo(() => getWeekDays(weekStart), [weekStart]);

  const entriesByDay = useMemo(() => {
    const map: Map<number, any[]> = new Map();
    weekDays.forEach((_, i) => map.set(i, []));

    for (const entry of entries) {
      const entryDate = new Date(entry.date);
      for (let i = 0; i < weekDays.length; i++) {
        if (isSameDay(entryDate, weekDays[i])) {
          map.get(i)!.push(entry);
          break;
        }
      }
    }
    return map;
  }, [entries, weekDays]);

  // Separate scheduled (has a startTime) vs unscheduled entries
  const { scheduled, unscheduled } = useMemo(() => {
    const s: any[] = [];
    const u: any[] = [];
    for (const entry of entries) {
      const startTime =
        entry.startTime ||
        (entry.timer?.startedAt
          ? (() => {
              const d = new Date(entry.timer.startedAt);
              return `${String(d.getHours()).padStart(2, "0")}:${String(
                d.getMinutes()
              ).padStart(2, "0")}`;
            })()
          : null);
      if (startTime) {
        s.push({ ...entry, _resolvedStartTime: startTime });
      } else {
        u.push(entry);
      }
    }
    return { scheduled: s, unscheduled: u };
  }, [entries]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const getMatterTitle = useCallback(
    (matterId: string) => {
      const m = matters.find((mat: any) => mat._id === matterId);
      return m?.title || m?.matterNumber || "Unknown";
    },
    [matters]
  );

  /** Navigate to the Monday of the current real-world week */
  const goToToday = useCallback(() => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    setWeekStart(new Date(now.getFullYear(), now.getMonth(), diff));
  }, [setWeekStart]);

  const goToPrevWeek = useCallback(() => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(d);
  }, [weekStart, setWeekStart]);

  const goToNextWeek = useCallback(() => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(d);
  }, [weekStart, setWeekStart]);

  // ── renderDayColumn ────────────────────────────────────────────────────────

  const renderDayColumn = useCallback(
    (day: Date, dayIndex: number) => {
      // Filter scheduled entries to this day
      const dayEntries = scheduled.filter((e: any) =>
        isSameDay(new Date(e.date), day)
      );

      // Build overlap data structures
      const overlapEntries = dayEntries.map((e: any) => {
        const [h, m] = (e._resolvedStartTime || "09:00")
          .split(":")
          .map(Number);
        const startMin = h * 60 + m;
        return {
          _id: e._id,
          startMinutes: startMin,
          endMinutes: startMin + e.minutes,
        };
      });
      const overlapLayout = computeTimeEntryOverlap(overlapEntries);

      const today = isToday(day);

      // ── Drag-create mouse handler (background click) ─────────────────────

      const handleColumnMouseDown = (e: React.MouseEvent) => {
        // Ignore clicks on existing entry blocks
        if ((e.target as HTMLElement).closest("[data-entry-block]")) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const y =
          e.clientY - rect.top + (gridRef.current?.scrollTop || 0);
        const rawMinutes = (y / HOUR_HEIGHT) * 60;
        const snappedMinutes = snapToGrid(rawMinutes);

        dragCreateRef.current = { startY: e.clientY };
        setDragCreate({
          dayIndex,
          startMinutes: snappedMinutes,
          currentMinutes: snappedMinutes,
        });

        const handleMouseMove = (ev: MouseEvent) => {
          const deltaY = ev.clientY - dragCreateRef.current!.startY;
          const deltaMinutes = (deltaY / HOUR_HEIGHT) * 60;
          const newMinutes = snapToGrid(snappedMinutes + deltaMinutes);
          setDragCreate((prev) =>
            prev
              ? {
                  ...prev,
                  currentMinutes: Math.max(
                    0,
                    Math.min(newMinutes, TOTAL_HOURS * 60)
                  ),
                }
              : null
          );
        };

        const handleMouseUp = (ev: MouseEvent) => {
          document.removeEventListener("mousemove", handleMouseMove);
          document.removeEventListener("mouseup", handleMouseUp);
          document.body.style.cursor = "";

          setDragCreate((prev) => {
            if (!prev) return null;
            const start = Math.min(prev.startMinutes, prev.currentMinutes);
            const end = Math.max(prev.startMinutes, prev.currentMinutes);
            const duration = Math.max(end - start, 15);

            const rect2 =
              columnRefs.current[dayIndex]?.getBoundingClientRect();
            setQuickCreateData({
              date: toISODateStr(weekDays[dayIndex]),
              startTime: minutesToTimeStr(start),
              minutes: duration,
              anchorX: rect2
                ? rect2.left + rect2.width / 2
                : ev.clientX,
              anchorY: Math.min(ev.clientY, window.innerHeight - 300),
            });

            return null;
          });
        };

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "crosshair";
      };

      // ── Entry mouse handler (move / resize / click) ──────────────────────

      const handleEntryMouseDown = (
        e: React.MouseEvent,
        entry: any
      ) => {
        e.stopPropagation();
        const isBilled = entry.status === "billed";

        // Billed entries are read-only; just select them
        if (isBilled) {
          onSelectEntry(entry);
          return;
        }

        const rect = (
          e.currentTarget as HTMLElement
        ).getBoundingClientRect();
        const isBottomEdge = e.clientY > rect.bottom - 8;

        // ── Resize (bottom-edge drag) ────────────────────────────────────

        if (isBottomEdge) {
          const startY = e.clientY;
          const origMin = entry.minutes;

          const onMove = (ev: MouseEvent) => {
            const deltaY = ev.clientY - startY;
            const deltaMinutes = snapToGrid(
              (deltaY / HOUR_HEIGHT) * 60
            );
            const newMin = Math.max(
              MIN_BLOCK_MINUTES,
              origMin + deltaMinutes
            );
            setDragResize({
              entryId: entry._id,
              originalMinutes: origMin,
              newMinutes: newMin,
            });
          };

          const onUp = () => {
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
            document.body.style.cursor = "";

            setDragResize((prev) => {
              if (prev && prev.newMinutes !== origMin) {
                timeEntriesApi.update(entry._id, {
                  minutes: prev.newMinutes,
                });
                setUndoAction({
                  message: `Duration changed to ${formatDuration(
                    prev.newMinutes
                  )}`,
                  rollback: async () => {
                    await timeEntriesApi.update(entry._id, {
                      minutes: origMin,
                    });
                    onRefresh();
                  },
                });
                onRefresh();
              }
              return null;
            });
          };

          document.addEventListener("mousemove", onMove);
          document.addEventListener("mouseup", onUp);
          document.body.style.cursor = "ns-resize";
          return;
        }

        // ── Move or click ────────────────────────────────────────────────

        const startY2 = e.clientY;
        const startX2 = e.clientX;
        let didDrag = false;
        const colWidth =
          columnRefs.current[dayIndex]?.getBoundingClientRect().width ||
          100;

        const onMove2 = (ev: MouseEvent) => {
          const dx = Math.abs(ev.clientX - startX2);
          const dy = Math.abs(ev.clientY - startY2);
          if (dx > 3 || dy > 3) didDrag = true;
          if (!didDrag) return;

          const deltaY = ev.clientY - startY2;
          const deltaMinutes = snapToGrid(
            (deltaY / HOUR_HEIGHT) * 60
          );
          const deltaX = ev.clientX - startX2;
          const dayOff = Math.round(deltaX / colWidth);

          setDragMove({
            entryId: entry._id,
            offsetMinutes: deltaMinutes,
            dayOffset: dayOff,
          });
          document.body.style.cursor = "grabbing";
        };

        const onUp2 = () => {
          document.removeEventListener("mousemove", onMove2);
          document.removeEventListener("mouseup", onUp2);
          document.body.style.cursor = "";

          if (!didDrag) {
            onSelectEntry(entry);
            setDragMove(null);
            return;
          }

          setDragMove((prev) => {
            if (!prev) return null;

            // Calculate new startTime and date
            const [h, m] = (entry._resolvedStartTime || "09:00")
              .split(":")
              .map(Number);
            const origStartMin = h * 60 + m;
            const newStartMin = Math.max(
              0,
              Math.min(
                origStartMin + prev.offsetMinutes,
                (TOTAL_HOURS - 1) * 60
              )
            );
            const newStartTime = minutesToTimeStr(newStartMin);

            const origDate = new Date(entry.date);
            const newDate = new Date(weekDays[dayIndex]);
            newDate.setDate(newDate.getDate() + prev.dayOffset);
            const newDateStr = toISODateStr(newDate);

            timeEntriesApi.update(entry._id, {
              startTime: newStartTime,
              date: newDateStr,
            });

            setUndoAction({
              message: `Moved to ${formatShortDate(newDate)}, ${newStartTime}`,
              rollback: async () => {
                await timeEntriesApi.update(entry._id, {
                  startTime:
                    entry._resolvedStartTime || entry.startTime,
                  date: toISODateStr(origDate),
                });
                onRefresh();
              },
            });
            onRefresh();
            return null;
          });
        };

        document.addEventListener("mousemove", onMove2);
        document.addEventListener("mouseup", onUp2);
      };

      // ── Render ─────────────────────────────────────────────────────────

      return (
        <div
          key={dayIndex}
          ref={(el) => {
            columnRefs.current[dayIndex] = el;
          }}
          className={`flex-1 relative ${
            dayIndex < 6 ? "border-r border-slate-200" : ""
          } ${today ? "bg-emerald-50/20" : ""}`}
          style={{ minHeight: `${TOTAL_HOURS * HOUR_HEIGHT}px` }}
          onMouseDown={handleColumnMouseDown}
        >
          {/* Hour grid cells */}
          {Array.from({ length: TOTAL_HOURS }, (_, i) => (
            <div
              key={i}
              className="relative border-b border-slate-100"
              style={{ height: `${HOUR_HEIGHT}px` }}
            >
              {/* Quarter-hour dashed lines */}
              {[1, 2, 3].map((q) => (
                <div
                  key={q}
                  className="absolute left-0 right-0 border-t border-dashed border-slate-100/60"
                  style={{ top: `${q * (HOUR_HEIGHT / 4)}px` }}
                />
              ))}
            </div>
          ))}

          {/* Current time indicator (today only) */}
          {today && (
            <div
              className="absolute left-0 right-0 z-20 pointer-events-none"
              style={{ top: `${currentTimeTop}px` }}
            >
              <div className="relative flex items-center">
                <div className="h-3 w-3 rounded-full bg-red-500 -ml-1.5 shadow-sm" />
                <div className="flex-1 h-[2px] bg-red-500" />
              </div>
            </div>
          )}

          {/* Entry blocks */}
          {dayEntries.map((entry: any) => {
            const pos = getEntryPosition(
              entry._resolvedStartTime,
              entry.minutes
            );
            if (pos.top < 0) return null;

            const overlap = overlapLayout.get(entry._id) || {
              left: 0,
              width: 100,
            };
            const color = getMatterColor(
              entry.matterId?._id || entry.matterId || ""
            );
            const isBilled = entry.status === "billed";
            const isSelected = selectedEntry?._id === entry._id;

            // Apply drag-move offset when this entry is being dragged
            let adjustedTop = pos.top;
            if (dragMove?.entryId === entry._id) {
              adjustedTop +=
                (dragMove!.offsetMinutes / 60) * HOUR_HEIGHT;
            }

            // Apply drag-resize height when this entry is being resized
            let adjustedHeight = pos.height;
            if (dragResize?.entryId === entry._id) {
              adjustedHeight =
                (dragResize!.newMinutes / 60) * HOUR_HEIGHT;
            }

            return (
              <div
                key={entry._id}
                data-entry-block
                className={`absolute rounded-lg border-l-[3px] ${color.border} ${color.bg} overflow-hidden group z-10
                  ${
                    isBilled
                      ? "opacity-60 cursor-default"
                      : "cursor-grab hover:shadow-md"
                  }
                  ${
                    isSelected
                      ? "ring-2 ring-emerald-500 shadow-md"
                      : ""
                  }
                  ${
                    dragMove?.entryId === entry._id
                      ? "shadow-lg ring-2 ring-emerald-400/50 z-20 opacity-90 cursor-grabbing"
                      : ""
                  }`}
                style={{
                  top: `${adjustedTop}px`,
                  height: `${Math.max(adjustedHeight, 15)}px`,
                  left: `calc(${overlap.left}% + 2px)`,
                  width: `calc(${overlap.width}% - 4px)`,
                }}
                onMouseDown={(e) => handleEntryMouseDown(e, entry)}
              >
                <div className="px-2 py-1 h-full flex flex-col justify-between select-none">
                  <div className="min-w-0">
                    <p
                      className={`text-[11px] font-semibold ${color.text} truncate leading-tight`}
                    >
                      {getMatterTitle(
                        entry.matterId?._id || entry.matterId
                      )}
                    </p>
                    {adjustedHeight > 35 && (
                      <p className="text-[10px] text-slate-500 truncate mt-0.5">
                        {entry.narrative}
                      </p>
                    )}
                  </div>
                  {adjustedHeight > 25 && (
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-slate-400">
                        {formatDuration(
                          dragResize?.entryId === entry._id
                            ? dragResize!.newMinutes
                            : entry.minutes
                        )}
                      </span>
                      {!entry.billable && (
                        <span className="text-[9px] bg-slate-200 text-slate-500 px-1 rounded">
                          NB
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Resize handle (bottom edge) */}
                {!isBilled && (
                  <div className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize opacity-0 group-hover:opacity-100 bg-gradient-to-t from-slate-300/30 transition-opacity" />
                )}
              </div>
            );
          })}

          {/* Drag-create preview block */}
          {dragCreate && dragCreate.dayIndex === dayIndex && (
            <div
              className="absolute rounded-lg border-2 border-dashed border-emerald-400 bg-emerald-50/70 z-20 pointer-events-none"
              style={{
                top: `${
                  (Math.min(
                    dragCreate.startMinutes,
                    dragCreate.currentMinutes
                  ) /
                    60) *
                  HOUR_HEIGHT
                }px`,
                height: `${Math.max(
                  (Math.abs(
                    dragCreate.currentMinutes -
                      dragCreate.startMinutes
                  ) /
                    60) *
                    HOUR_HEIGHT,
                  15
                )}px`,
                left: "2px",
                right: "2px",
              }}
            >
              <div className="px-2 py-1">
                <p className="text-xs font-semibold text-emerald-700">
                  {minutesToTimeStr(
                    Math.min(
                      dragCreate.startMinutes,
                      dragCreate.currentMinutes
                    )
                  )}{" "}
                  &mdash;{" "}
                  {minutesToTimeStr(
                    Math.max(
                      dragCreate.startMinutes,
                      dragCreate.currentMinutes
                    )
                  )}
                </p>
                <p className="text-[10px] text-emerald-600">
                  {formatDuration(
                    Math.abs(
                      dragCreate.currentMinutes -
                        dragCreate.startMinutes
                    )
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
      );
    },
    [
      scheduled,
      weekDays,
      currentTimeTop,
      dragCreate,
      dragMove,
      dragResize,
      selectedEntry,
      onSelectEntry,
      onRefresh,
      getMatterTitle,
    ]
  );

  // ── Main render ────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full bg-white rounded-xl border border-slate-200 overflow-hidden relative">
      {/* ── Header: Week navigation ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50/50">
        <div className="flex items-center gap-2">
          <button
            onClick={goToPrevWeek}
            className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors"
          >
            <ChevronLeft className="h-4 w-4 text-slate-600" />
          </button>
          <span className="text-sm font-semibold text-slate-700 min-w-[180px] text-center">
            {formatShortDate(weekDays[0])} &mdash;{" "}
            {formatShortDate(weekDays[6])},{" "}
            {weekDays[0].getFullYear()}
          </span>
          <button
            onClick={goToNextWeek}
            className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors"
          >
            <ChevronRight className="h-4 w-4 text-slate-600" />
          </button>
        </div>
        <button
          onClick={goToToday}
          className="px-3 py-1.5 text-xs font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
        >
          Today
        </button>
      </div>

      {/* ── Unscheduled entries bar ─────────────────────────────────────────── */}
      {unscheduled.length > 0 && (
        <div className="px-4 py-2 border-b border-slate-100 bg-amber-50/50">
          <div className="flex items-center gap-2">
            <Clock className="h-3.5 w-3.5 text-amber-600" />
            <p className="text-xs font-medium text-amber-700">
              {unscheduled.length} unscheduled{" "}
              {unscheduled.length === 1 ? "entry" : "entries"} &mdash;
              drag onto calendar to schedule
            </p>
          </div>
        </div>
      )}

      {/* ── Day headers ─────────────────────────────────────────────────────── */}
      <div className="flex border-b border-slate-200">
        {/* Gutter spacer */}
        <div className="w-14 flex-shrink-0" />
        {weekDays.map((day, i) => (
          <div
            key={i}
            className={`flex-1 text-center py-2 ${
              i < 6 ? "border-r border-slate-100" : ""
            } ${isToday(day) ? "bg-emerald-50/50" : ""}`}
          >
            <p
              className={`text-xs font-medium ${
                isToday(day) ? "text-emerald-700" : "text-slate-500"
              }`}
            >
              {formatWeekday(day)}
            </p>
            <p
              className={`text-sm font-bold ${
                isToday(day) ? "text-emerald-700" : "text-slate-700"
              }`}
            >
              {day.getDate()}
            </p>
          </div>
        ))}
      </div>

      {/* ── Scrollable grid area ────────────────────────────────────────────── */}
      <div
        ref={gridRef}
        className="flex-1 overflow-y-auto overflow-x-hidden relative"
      >
        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-40">
            <Loader2 className="h-6 w-6 text-emerald-600 animate-spin" />
          </div>
        )}

        <div
          className="flex"
          style={{ minHeight: `${TOTAL_HOURS * HOUR_HEIGHT}px` }}
        >
          {/* Time gutter (hours column) */}
          <div className="w-14 flex-shrink-0 border-r border-slate-200">
            {Array.from({ length: TOTAL_HOURS }, (_, i) => (
              <div
                key={i}
                className="relative border-b border-slate-100"
                style={{ height: `${HOUR_HEIGHT}px` }}
              >
                <span className="absolute -top-2.5 right-2 text-[10px] font-medium text-slate-400">
                  {formatHourLabel(START_HOUR + i)}
                </span>
              </div>
            ))}
          </div>

          {/* Day columns */}
          {weekDays.map((day, dayIndex) =>
            renderDayColumn(day, dayIndex)
          )}
        </div>
      </div>

      {/* ── Undo toast ──────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {undoAction && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-slate-800 text-white px-4 py-2.5 rounded-xl shadow-lg flex items-center gap-3 text-sm z-50"
          >
            <span>{undoAction.message}</span>
            <button
              onClick={() => {
                undoAction.rollback();
                setUndoAction(null);
              }}
              className="font-bold text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              Undo
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Quick-create popover ─────────────────────────────────────────────── */}
      {quickCreateData && (
        <TimeCalendarQuickCreate
          date={quickCreateData.date}
          startTime={quickCreateData.startTime}
          minutes={quickCreateData.minutes}
          anchorX={quickCreateData.anchorX}
          anchorY={quickCreateData.anchorY}
          matters={matters}
          clients={clients}
          onClose={() => setQuickCreateData(null)}
          onCreated={() => {
            setQuickCreateData(null);
            onRefresh();
          }}
        />
      )}

      {/* ── Entry detail panel ──────────────────────────────────────────────── */}
      <TimeCalendarEntryDetail
        entry={selectedEntry}
        matters={matters}
        clients={clients}
        onClose={() => onSelectEntry(null)}
        onUpdated={onRefresh}
        onDeleted={onRefresh}
      />
    </div>
  );
};

export default TimeCalendarView;
